import { rem } from 'polished'
import React, { useCallback } from 'react'
import styled from 'styled-components'

import { DetailItemCardVariant } from './types'

import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import ItemMenuOptions from 'tripPlanner/components/ItemCard/Common/ItemMenuOptions'
import SavedItemRemoveButton from 'tripPlanner/components/ItemCard/Common/SavedItemRemoveButton'
import { AddRemoveItineraryButton } from 'tripPlanner/components/Itinerary/AddRemoveItineraryButton'
import ItineraryDateButton from 'tripPlanner/components/Itinerary/ItineraryDateButton'
import EditTripItem from 'tripPlanner/components/TripModal/TripItems/Edit'
import EditTripDates from 'tripPlanner/components/TripModal/Trips/EditTripDatesTP'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import MenuButton from 'tripPlanner/Tripkit/Core/MenuButton/MenuButton'
import { TripItem } from 'tripPlanner/types/tripItem'
import { isCustomItem } from 'tripPlanner/utils'
import BookedLabel from 'tripPlanner/components/BookedLabel'
import { mediaQueryDown } from 'components/utils/breakpoint'

const FloatRight = styled.div`
  float: right;
  padding-left: ${rem(8)};
  padding-bottom: ${rem(4)};
`

const LargeDesktopOnlyBookedLabel = styled(BookedLabel)`
  ${mediaQueryDown.desktop} {
    display: none;
  }
`

function ItineraryButtonWrapper({
  variant,
  children,
}: React.PropsWithChildren<{ variant: DetailItemCardVariant }>) {
  if (variant === 'drawer') {
    return null
  }
  if (variant === 'itinerary') {
    return <CSSBreakpoint min="tablet">{children}</CSSBreakpoint>
  }
  return <>{children}</>
}

function CornerControls({
  item,
  variant,
}: {
  item: TripItem
  variant: DetailItemCardVariant
}) {
  const openModal = useOpenModal()

  const editItem = useCallback(
    () => openModal(EditTripItem, { item }),
    [item, openModal],
  )

  const onRequestAddTripDates = useCallback(
    (
      onDatesAdded?: (startDate: moment.Moment, endDate: moment.Moment) => void,
    ) => {
      openModal(EditTripDates, { tripId: item.tripId, onDatesAdded })
    },
    [openModal, item.tripId],
  )

  const isBooked = 'isBooked' in item && item.isBooked

  return (
    <FloatRight>
      <Group direction="horizontal" gap={8} verticalAlign="center">
        {isBooked && <LargeDesktopOnlyBookedLabel />}
        {isCustomItem(item) && (
          <>
            <ItineraryButtonWrapper variant={variant}>
              <ItineraryDateButton
                item={item}
                variant="chip"
                onRequestAddTripDates={onRequestAddTripDates}
              />
            </ItineraryButtonWrapper>
            <MenuButton
              title=""
              kind="secondary"
              variant="dark"
              shape="circle"
              bodyContent={<ItemMenuOptions item={item} />}
            />
          </>
        )}
        {!isCustomItem(item) && (
          <>
            <ItineraryButtonWrapper variant={variant}>
              <AddRemoveItineraryButton
                item={item}
                onClickAddTripItemDates={editItem}
              />
            </ItineraryButtonWrapper>
            <SavedItemRemoveButton item={item} />
          </>
        )}
      </Group>
    </FloatRight>
  )
}

export default CornerControls
